import type { User } from '@engagement-web-app/common';
import { QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { FileText, Trophy, Users } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { ClientAuthenticationProvider } from '@/auth/ClientAuthenticationProvider';
import { SessionExpiredEmptyState } from '@/components/SessionExpiredEmptyState';
import { showSessionExpiredToast } from '@/components/SessionExpiredToast';
import { type NavItem, Sidebar } from '@/components/Sidebar';
import { Topbar } from '@/components/Topbar';
import { type SessionExpiredSeverity, useAuthenticatedQueryClient } from '@/hooks';
import { ClientPermissionProvider } from '@/permissions/ClientPermissionProvider';
import { hasClientPermission } from '@/permissions/clientUserPermissions';
import { getLoginUrl, logout } from '@/queries/auth';
import { getCurrentUser } from '@/queries/users';
import { getFullName } from '@/utils';
import * as styles from './route.css';

export const Route = createFileRoute('/_client/_auth')({
    component: ClientRoute,
    // Re-checked on every genuine navigation into this subtree — but not on a
    // search-param-only `navigate()` that stays on the same already-active route
    // (e.g. changing a filter dropdown), which would otherwise re-run this on every
    // such interaction and could flash the full-screen empty state mid-interaction.
    // This route's own `cause` can't tell the two apart (it's 'stay' for both, since
    // this layout's own match never changes), but the full `matches` array can: a
    // genuine navigation always has at least one match entering fresh.
    beforeLoad: async ({ matches }) => {
        const isRealNavigation = matches.some((m) => m.cause === 'enter');
        if (!isRealNavigation) return;

        try {
            const user = await getCurrentUser();
            return { user };
        } catch {
            return { user: null };
        }
    },
});

function ClientRoute() {
    // `beforeLoad` returns `undefined` (no `user` key) when skipping a search-param-only
    // navigation, so TanStack Router can't statically prove `user` is always present —
    // it always is by the time this renders, since the very first entry into this
    // subtree is guaranteed to hit the real fetch (see `beforeLoad` above).
    const { user } = Route.useRouteContext() as { user: User | null };
    const { queryClient, severity } = useAuthenticatedQueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <ClientApp user={user} severity={severity} />
        </QueryClientProvider>
    );
}

function SessionExpiredRedirect() {
    useEffect(() => {
        window.location.href = getLoginUrl('client');
    }, []);
    return null;
}

type ClientAppProps = {
    user: User | null;
    severity: SessionExpiredSeverity;
};

function ClientApp({ user, severity }: ClientAppProps) {
    const navigate = useNavigate();
    const isRoot = !!useMatch({ from: '/_client/_auth/', shouldThrow: false });

    useEffect(() => {
        if (severity !== 'none') showSessionExpiredToast(severity, 'client');
    }, [severity]);

    // Reports and user management are admin-only; hide their nav entries for members.
    const navItems = useMemo<NavItem[]>(() => {
        if (!user) return [];
        return [
            // After discussing with our PO, we decided to hide dashboard for now
            // for client users, since we have nothing to show on the dashboard yet.
            // Instead, they will be redirected to `/surveys` below.
            // { to: '/', icon: LayoutDashboard, label: 'ダッシュボード', exact: true },
            {
                to: '/surveys',
                icon: Trophy,
                label: '結果分析',
                children: [
                    { to: '/surveys/summaries', label: '全社' },
                    { to: '/surveys/categories', label: 'カテゴリー' },
                    { to: '/surveys/questions', label: '設問' },
                    { to: '/surveys/comments', label: 'コメント' },
                ],
            },
            ...(hasClientPermission(user.role.id, 'reports', 'view')
                ? [{ to: '/reports', icon: FileText, label: 'レポート' } satisfies NavItem]
                : []),
            ...(hasClientPermission(user.role.id, 'users', 'view')
                ? [{ to: '/users', icon: Users, label: 'ユーザー' } satisfies NavItem]
                : []),
        ];
    }, [user]);

    if (user === null) {
        if (isRoot) return <SessionExpiredRedirect />;
        return <SessionExpiredEmptyState kind="client" />;
    }

    return (
        <ClientAuthenticationProvider user={user}>
            <ClientPermissionProvider roleId={user.role.id}>
                <Sidebar navItems={navItems} />
                <div className={styles.content}>
                    <Topbar
                        userName={getFullName(user)}
                        mode="client"
                        onMyPageClick={() => navigate({ to: '/profile' })}
                        onLogoutClick={logout}
                    />
                    <main className={styles.main}>
                        <Outlet />
                    </main>
                </div>
            </ClientPermissionProvider>
        </ClientAuthenticationProvider>
    );
}
